//Hooks
import { get } from 'svelte/store';
//Helpers
import { home_data } from 'src/stores';
import { isPremiumUser } from 'src/helpers/user_helpers';
import { addDate, startsWeekOnSun, timeFormatUS } from 'src/helpers/date_helper';
import { getDeviceTimeZoneDate } from '../core/chart_helper';
import { is_GBLE } from 'src/helpers/interface_helpers';
//Types
import type { ChartConfig, ChartStatisticRangePeriod, TimeZoneInterface } from 'src/types/Chart';
import type { Device } from 'src/types/Device';

export interface RangeOptionInterface {
	period: ChartStatisticRangePeriod;
	timeRange: string;
	timeLabelFormat: string;
	enabled: boolean;
	label?: string;
}

export interface ChartControlsDataInterface {
	time: { from: Date; to: Date };
	format: string;
	rangeOption: RangeOptionInterface;
	currentPeriod: string;
}

const TIME_LABEL_FORMATS = {
	hour: 'H:i',
	day: 'H:i',
	week: 'D d M',
	month: 'd.M',
	day_splitter: 'd.M',
	year: 'M`Y',
	day_of_week: 'D',
	tariff_day_of_week: 'D H:i',
	'7_days': 'D d M',
	'30_days': 'd.M',
	'6_months': 'M`Y',
	'12_months': 'M`Y',
};

export const RANGE_OPTIONS: Array<RangeOptionInterface> = [
	{
		period: '24_hours',
		timeRange: '24h',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'day',
		timeRange: '1d',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'hour',
		timeRange: '1h',
		timeLabelFormat: 'hour',
		enabled: true,
	},

	{
		period: '7_days',
		timeRange: '7d',
		timeLabelFormat: 'week',
		enabled: true,
	},

	{
		period: 'week',
		timeRange: '1w',
		timeLabelFormat: 'week',
		enabled: true,
	},
	{
		period: '30_days',
		timeRange: '30d',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: 'month',
		timeRange: '1m',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: '12_months',
		timeRange: '12m',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'year',
		timeRange: '1y',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'billing_date',
		timeRange: '1m',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: 'custom',
		timeRange: 'custom',
		timeLabelFormat: 'custom',
		enabled: true,
	},
];

export const getTimeLabelFormat = (timeLabelFormat: string) => {
	let timeFormat = TIME_LABEL_FORMATS[timeLabelFormat] || TIME_LABEL_FORMATS.day;
	if (/H:i$/.test(timeFormat) && timeFormatUS()) {
		timeFormat = timeFormat.replace('H', 'h') + ' A';
	}
	return timeFormat;
};

/**
 * @param date : Date => current date we calculate from
 * @param rangeOption : RangeOptionInterface
 * @param action : string | [subtract, add, init]
 * @returns {from: Date, to: Date}
 */
export const rangeDates = (
	date: Date,
	rangeOption: RangeOptionInterface,
	action: 'subtract' | 'add' | 'init' | 'click' = 'init',
	subtractOneSecond = false,
): { from: Date; to: Date } => {
	const init =
			action == 'init' && /^(24_hours|7_days|30_days|6_months|12_months)$/.test(rangeOption.period)
				? -1
				: 0,
		actions = { subtract: -1, add: 1, init, click: 0 },
		t = rangeOption.timeRange.split(/(\d+)/),
		timeValue = Number(t[0] + t[1]),
		timeRange = t[2],
		startWeekOnSun = /^w(eek)?/.test(timeRange) && startsWeekOnSun();
	let from = addDate(
		date,
		timeRange,
		timeValue * actions[action],
		!/^(billing_date)$/.test(rangeOption.period),
		startWeekOnSun,
	);
	if (init == -1) from = addDate(from, timeRange, 1, false, startWeekOnSun);
	let to = addDate(from, timeRange, timeValue, false, startWeekOnSun);
	if (subtractOneSecond) to = addDate(to, 's', -1);
	return { from, to };
};

export const getInitLoadedPeriodStatistics = (
	rangeOpt: RangeOptionInterface[],
): RangeOptionInterface => {
	let rangeOption: RangeOptionInterface = rangeOpt[0];
	const initPeriodKey = get(home_data)?.init_loaded_period_statistics;
	if (initPeriodKey) {
		rangeOption = rangeOpt.find((i) => i.period == initPeriodKey);
		if (!rangeOption?.enabled) rangeOption = rangeOpt[0];
	}
	return rangeOption;
};

export const isPrevPeriodComparisonEnabled = (): boolean => {
	return get(home_data)?.prev_period_comparison?.value;
};

export const getBillingDate = (): Date => {
	const today = new Date(),
		billingDate = get(home_data)?.billing_date || 1;
	let date = new Date();
	if (today.getDate() < billingDate) {
		date = addDate(today, 'm', -1);
	}
	date.setDate(billingDate);
	return date;
};

export const getEnabledHourControl = (): boolean => {
	return isPremiumUser();
};

export const LAYER_RANGE_OPTIONS: Array<RangeOptionInterface> = [
	{
		period: 'hour',
		timeRange: '1h',
		label: 'H',
		timeLabelFormat: 'hour',
		enabled: true,
	},
	{
		period: '24_hours',
		timeRange: '24h',
		label: '24H',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'day',
		timeRange: '1d',
		label: '1D',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'week',
		timeRange: '1w',
		label: '1W',
		timeLabelFormat: 'week',
		enabled: true,
	},
	{
		period: '7_days',
		timeRange: '7d',
		label: '7D',
		timeLabelFormat: 'week',
		enabled: true,
	},
	{
		period: 'month',
		timeRange: '1m',
		label: '1M',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: '6_months',
		timeRange: '6m',
		label: '6M',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: '12_months',
		timeRange: '12m',
		label: '12M',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'custom',
		timeRange: 'custom',
		label: 'custom',
		timeLabelFormat: 'custom',
		enabled: true,
	},
	{
		period: 'billing_date',
		timeRange: '1m',
		label: 'billing',
		timeLabelFormat: 'month',
		enabled: true,
	},
];

export const buildChartControlsData = (
	device: Device,
	currentPeriod: ChartStatisticRangePeriod,
): ChartControlsDataInterface => {
	const rangeOption = LAYER_RANGE_OPTIONS.find((i) => i.period == currentPeriod);
	const timeZone: TimeZoneInterface = getDeviceTimeZoneDate(device);
	let date = timeZone?.date || new Date();
	if (currentPeriod == 'billing_date') {
		date = addDate(getBillingDate(), 'd', 0, true);
	}
	const time = rangeDates(date, rangeOption, 'init', true);
	const chartControlsData: ChartControlsDataInterface = {
		time,
		format: 'Y-m-d H:i:s',
		rangeOption,
		currentPeriod,
	};
	return chartControlsData;
};

export type ChartMenuItem = {
	value: number;
	icon: string;
	title: string;
};

export const buildChartHeaderMenu = (chartConfigs: ChartConfig[]): ChartMenuItem[] => {
	return chartConfigs.map((item, index) => ({
		value: index,
		icon: item.icon,
		title: item.chartTitle,
	}));
};

export const isBleStatisticsDeviceToUTC = (
	device: Device,
	period: ChartStatisticRangePeriod,
): boolean => {
	return is_GBLE(device) && /^(24_hours|day|hour)$/.test(period);
};
